import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import React, { useCallback } from "react";
import moment from "moment";
import { ActionIcon, Image, Text, Center } from "@mantine/core";

import {
  EmployeeTicketsListProps,
  EmployeeTicket,
  QueryCategory,
} from "../utils/types";
import { getQueryIcon } from "../utils/utils";

const EmployeeTicketsList: React.FC<EmployeeTicketsListProps> = ({
  page,
  pages,
  count,
  tickets = [],
  ticketId,
  setTicketId,
  setPage,
  searchQuery,
}): JSX.Element => {
  const handleUpdateTicket = (ticketID: string): void => {
    setTicketId(ticketID);
  };

  const getTicketDate = (ticket: EmployeeTicket): string => {
    if (ticket.data) {
      const startDate = ticket.data.find((data) => data.key === "start_date");
      const endDate = ticket.data.find((data) => data.key === "end_date");
      if (startDate && endDate) {
        return `${moment(startDate.value).format("DD/MM/YYYY")} - ${moment(
          endDate.value
        ).format("DD/MM/YYYY")}`;
      }
    }
    return moment(ticket.date_created).format("DD/MM/YYYY");
  };

  const getPaginationDisplay = useCallback((): string => {
    if (tickets?.length === 0) {
      return "0 of 0";
    }

    const startIndex = (page - 1) * 10 + 1;
    const endIndex = Math.min(page * 10, count);

    return `${startIndex} - ${endIndex} of ${count}`;
  }, [page, count, tickets]);

  const getQueryTypeLabel = (ticket: EmployeeTicket): string => {
    if (ticket?.category === QueryCategory.LEAVE) {
      return ticket.query_type;
    }

    return `${ticket.query_type} Query`;
  };

  const getEmployeeIdentity = (ticket: EmployeeTicket): string => {
    const employeeLastName = ticket.employee?.last_name || ticket.last_name;
    const employeeNumber = ticket.employee?.employee_no || ticket.employee_no;
    const employeeFirstName = ticket.employee?.first_name || ticket.first_name;
    return `${employeeNumber} - ${employeeFirstName} ${employeeLastName}`;
  };

  const getEmployerBranchName = (ticket: EmployeeTicket): string => {
    const employerBranchName =
      ticket.employee?.employee_branch?.name || ticket.employer_branch_name;
    return employerBranchName ?? "No Branch";
  };

  const renderEmptyState = (): JSX.Element => {
    if (searchQuery && searchQuery.length >= 2) {
      return (
        <Center className="h-full">
          <div className="flex flex-col items-center gap-4 text-center">
            <Search size={48} className="text-gray-400" />
            <div>
              <Text size="lg" weight={600} className="text-jem-primary-base">
                No results found
              </Text>
              <Text size="sm" className="text-jem-primary-50t">
                No tickets match your search for &quot;{searchQuery}&quot;
              </Text>
            </div>
          </div>
        </Center>
      );
    }

    return (
      <Center className="h-full">
        <div className="flex flex-col items-center gap-4 text-center">
          <Text size="lg" weight={600} className="text-jem-primary-base">
            No tickets available
          </Text>
          <Text size="sm" className="text-jem-primary-50t">
            There are no tickets to review at this time
          </Text>
        </div>
      </Center>
    );
  };

  return (
    <div className="flex-1 max-w-[411px] border-jem-shade border-r pr-8 mr-8 relative flex flex-col">
      <div className="relative flex flex-col h-full gap-1 pt-2 pb-2 overflow-y-auto">
        {tickets?.length === 0
          ? renderEmptyState()
          : tickets?.map((t) => (
              <button
                key={t.id}
                className={`font-roboto flex p-4 mx-2 gap-2 border-b border-jem-shade hover:bg-jem-shade/50 hover:border-none cursor-pointer hover:rounded-md ${
                  ticketId && t.id === ticketId && "bg-jem-shade rounded-md"
                }`}
                onClick={() => handleUpdateTicket(t.id)}
                onKeyDown={() => handleUpdateTicket(t.id)}
                aria-label={`${t.employee?.employee_no} - ${t.employee?.first_name} ${t.employee?.last_name}`}
                tabIndex={0}
              >
                <Image
                  src={getQueryIcon(
                    t.query_type,
                    t?.category ?? QueryCategory.DOCUMENT
                  )}
                  alt={`${t.query_type} Icon`}
                  title={`${t.query_type} Icon`}
                  width={24}
                  height={24}
                />
                <div className="flex flex-col flex-1 gap-1">
                  <div className="flex flex-row items-center justify-between">
                    <h3 className="flex-1 text-xs font-bold capitalize leading-none text-left text-jem-primary-base">
                      {getQueryTypeLabel(t)}
                    </h3>
                    <span
                      className={`px-3 h-6 flex items-center rounded-full capitalize ${
                        ["OPEN", "PENDING"].includes(t.status)
                          ? "text-[#14926D] bg-[#E8FAF5]"
                          : "bg-[#EDEDED] text-[#384D5C]"
                      } text-xs capitalize`}
                    >
                      {t.status.toLowerCase()}
                    </span>
                  </div>
                  <h3 className="text-sm font-bold text-left text-jem-primary-base line-clamp-1">
                    {getEmployeeIdentity(t)}
                  </h3>
                  <div className="flex flex-row justify-between">
                    <p className="text-xs text-jem-primary-50t">
                      {getEmployerBranchName(t)}
                    </p>
                    <p className="text-xs text-jem-primary-50t">
                      {getTicketDate(t)}
                    </p>
                  </div>
                </div>
              </button>
            ))}
      </div>
      {count > 0 && (
        <div className="sticky bottom-0 z-10 flex flex-row items-center justify-end gap-2 p-4 bg-white border-t border-jem-shade">
          <span className="text-jem-primary-50t">{getPaginationDisplay()}</span>
          <ActionIcon
            disabled={page === 1 || count === 0}
            onClick={() => setPage(page - 1)}
            aria-label="Previous Page"
            className="bg-transparent"
          >
            <ChevronLeft size={20} />
          </ActionIcon>
          <ActionIcon
            disabled={page === pages || count === 0}
            onClick={() => setPage(page + 1)}
            aria-label="Next Page"
            className="bg-transparent"
          >
            <ChevronRight size={20} />
          </ActionIcon>
        </div>
      )}
    </div>
  );
};

export default EmployeeTicketsList;
